<template>
    <section class="youtube-videos-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
            </div>

            <div class="youtube-carousel">
                <button
                    class="carousel-nav-btn carousel-nav-left"
                    @click="previousVideo"
                    :aria-label="t('common.previous')"
                >
                    <svg
                        class="w-8 h-8"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>

                <div class="videos-wrapper">
                    <div
                        class="videos-scroll"
                        :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }"
                    >
                        <div
                            v-for="(video, index) in videos"
                            :key="index"
                            class="video-iframe-container"
                        >
                            <iframe
                                :src="video.embedUrl"
                                :title="video.title"
                                class="video-iframe"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen
                            ></iframe>
                        </div>
                    </div>
                </div>

                <button
                    class="carousel-nav-btn carousel-nav-right"
                    @click="nextVideo"
                    :aria-label="t('common.next')"
                >
                    <svg
                        class="w-8 h-8"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useVideoSeo, type YouTubeVideo } from '~/composables/useVideoSeo'

interface Video {
    title: string
    embedUrl: string
    description?: string
    uploadDate?: string
    duration?: string
}

interface Props {
    title: string
    videos: Video[]
}

const props = defineProps<Props>()

const { t } = useI18n()

const currentVideoIndex = ref(0)

const nextVideo = () => {
    const maxVisibleIndex = Math.min(2, props.videos.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        const maxVisibleIndex = Math.min(2, props.videos.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// 注入YouTube视频结构化数据
// 为所有视频添加 VideoObject，使用 @graph 格式（符合Google最佳实践）
// 使用 watchEffect 确保在 props 变化时也能更新，并且在 SSR 时也能执行
watchEffect(() => {
    if (props.videos && props.videos.length > 0) {
        const youtubeVideos: YouTubeVideo[] = props.videos.map(v => ({
            title: v.title,
            embedUrl: v.embedUrl,
            description: v.description,
            uploadDate: v.uploadDate,
            duration: v.duration,
        }))
        // primaryOnly: false - 为所有视频添加结构化数据，最大化SEO效果
        useVideoSeo(youtubeVideos, { primaryOnly: false })
    }
})
</script>

<style lang="less" scoped>
.youtube-videos-section {
    padding: 6rem 0;
    background-color: #22242d;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.youtube-carousel {
    position: relative;
    max-width: 1400px;
    margin: 0 auto;
}

.carousel-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: rgba(255, 255, 255, 0.6);
    border: none;
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #585658;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.4);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
}

.carousel-nav-left {
    left: -1.25rem;
}

.carousel-nav-right {
    right: -1.25rem;
}

.videos-wrapper {
    overflow: hidden;
    border-radius: 0.75rem;
}

.videos-scroll {
    display: flex;
    transition: transform 0.5s ease;
    gap: 1.25rem;
}

.video-iframe-container {
    flex: 0 0 33.333%;
    min-width: 0;
}

.video-iframe {
    width: 100%;
    height: 200px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
}

@media (min-width: 640px) {
    .video-iframe {
        height: 14rem;
    }
}

@media (max-width: 768px) {
    .youtube-videos-section {
        padding: 4rem 0;
    }

    .section-title {
        font-size: 2rem;
        padding: 0 1rem;
    }

    .section-header {
        margin-bottom: 2rem;
        padding: 0 1rem;
    }

    .video-iframe-container {
        flex: 0 0 50%;
    }
}

@media (max-width: 480px) {
    .youtube-videos-section {
        padding: 3rem 0;
    }

    .section-title {
        font-size: 1.5rem;
    }

    .section-header {
        margin-bottom: 1.5rem;
    }

    .video-iframe-container {
        flex: 0 0 100%;
    }
}
</style>

